import type { TFunction } from "i18next"
import * as React from "react"
import { useTranslation } from "react-i18next"

import StatefulField from "shared/components/StatefulField"
import { ADDITIVES, ALLERGENS, INutrition } from "shared/hubrise/models"
import { TagOption } from "shared/hubrise/utils/tags"

import SelectWithNone from "./SelectWithNone"
import { AlcoholAndCaffeine } from "./Styles"

interface CompositionProps {
  nutrition: INutrition | null
  updateNutrition: (patch: Partial<INutrition>) => void
}

const Composition = ({ nutrition, updateNutrition }: CompositionProps): JSX.Element => {
  const { t } = useTranslation()

  const additiveOptions = (t: TFunction): Array<TagOption> =>
    ADDITIVES.map((value) => ({ label: t(`nutrition.additives.${value}`), value }))

  const allergenOptions = (t: TFunction): Array<TagOption> =>
    ALLERGENS.map((value) => ({ label: t(`nutrition.allergens.${value}`), value }))

  return (
    <>
      <StatefulField.Input
        initialValue={nutrition?.legal_name ?? ""}
        label={t("nutrition.component.composition.legal_name")}
        withMargin
        placeholder={t("nutrition.component.composition.legal_name_placeholder")}
        onChange={(value) => updateNutrition({ legal_name: value || null })}
      />

      <StatefulField.Input
        initialValue={nutrition?.ingredients?.join(", ") ?? ""}
        label={t("nutrition.component.composition.ingredients")}
        withMargin
        placeholder={t("nutrition.component.composition.ingredients_placeholder")}
        onChange={(value) => {
          const ingredients = value
            ? value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : null
          updateNutrition({ ingredients })
        }}
      />

      <SelectWithNone
        nutrition={nutrition}
        updateNutrition={updateNutrition}
        listKey="additives"
        label={t("nutrition.component.composition.additives_label")}
        options={additiveOptions(t)}
      />

      <SelectWithNone
        nutrition={nutrition}
        updateNutrition={updateNutrition}
        listKey="allergens"
        label={t("nutrition.component.composition.allergens_label")}
        options={allergenOptions(t)}
      />

      <AlcoholAndCaffeine>
        <StatefulField.Input
          initialValue={String(nutrition?.alcohol_percentage ?? "")}
          label={t("nutrition.component.values.alcohol_percentage")}
          withMargin
          suffix="%"
          onChange={(value) => {
            const num = parseFloat(value)
            updateNutrition({ alcohol_percentage: isNaN(num) ? null : num })
          }}
          filter={/^\d*\.?\d*$/}
        />
        <StatefulField.Input
          initialValue={String(nutrition?.caffeine_mg_per_100ml ?? "")}
          label={t("nutrition.component.values.caffeine_mg_per_100ml")}
          withMargin
          suffix="mg/100 ml"
          onChange={(value) => {
            const num = parseFloat(value)
            updateNutrition({ caffeine_mg_per_100ml: isNaN(num) ? null : num })
          }}
          filter={/^\d*\.?\d*$/}
        />
      </AlcoholAndCaffeine>
    </>
  )
}

export default Composition
